/**
 * LockedOverlay — blurs its children behind a centered "Upgrade to unlock" CTA
 * (SYR-68 Layer 1 element gating). Used to lock a section's BODY while its title
 * stays crisp, so the marketer can see what they're missing and why.
 *
 * When `locked` is false it renders children untouched.
 */

import React from 'react';
import { Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import { THEME } from '../config/theme';

interface LockedOverlayProps {
  locked: boolean;
  children: React.ReactNode;
  /** Optional custom CTA message. */
  message?: React.ReactNode;
}

export const LockedOverlay: React.FunctionComponent<LockedOverlayProps> = ({
  locked,
  children,
  message,
}) => {
  // Scoped navigate keeps the upgrade CTA inside the marketer-view shell
  // (rewrites /dashboard/organisation… → /marketer-view/:hash/admin/organisation…).
  const navigate = useScopedNavigate();

  if (!locked) {
    return <>{children}</>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          filter: 'blur(7px)',
          opacity: 0.4,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
        aria-hidden
      >
        {children}
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            textAlign: 'center',
            padding: '24px 32px',
            background: '#ffffff',
            border: `1px solid ${THEME.primarySoft}`,
            borderRadius: 16,
            boxShadow: '0 18px 48px -16px rgba(15,23,42,0.45)',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 999,
              background: THEME.primarySoft,
              color: THEME.primaryDark,
            }}
          >
            <LockOutlined style={{ fontSize: 20 }} />
          </span>
          <div style={{ fontWeight: 600, color: THEME.textBase }}>
            {message ?? (
              <FormattedMessage
                id="marketerView.lock.sectionTitle"
                defaultMessage="Upgrade to unlock this"
              />
            )}
          </div>
          <Button
            type="primary"
            size="small"
            onClick={() => navigate(MARKETER_UPGRADE_ROUTE)}
          >
            <FormattedMessage
              id="marketerView.lock.cta"
              defaultMessage="Upgrade to unlock"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
